import type { Field } from 'factorial-form'
import { ShiftManagementBulkPatchShiftsDocument } from 'generated/resources/graphql'

import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useRefreshStats } from 'modules/shift_management/hooks/refreshShiftsRows'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { toBreaksPayload } from 'modules/shift_management/lib/buildAddPlannedBreaksParameters'
import type PlannedBreakForm from 'modules/shift_management/lib/forms/PlannedBreakForm'
import { trackEditShiftSubmit } from 'modules/shift_management/lib/trackEvents'
import Shift from 'modules/shift_management/store/internal/models/Shift'

import { useSelectionDispatch } from './useSelection'
import { useShiftsRows } from './useShiftsRows'

export interface BulkPatchProps {
  shifts: Shift[]
  name: string | null
  startAt: string | null
  endAt: string | null
  notes: string | null
  extraHours: boolean | null
  locationId: string | null
  workAreaId: string | null
  mixedValueNotes: number
}

export const buildEditShiftsParameters = (
  shifts: Shift[],
  formName: Field,
  formStartAt: Field,
  formEndAt: Field,
  formExtraHours: boolean | null,
  formLocationId: Field,
  formWorkAreaId: Field,
  formNotes: Field,
  uniqNotesLength: number
): BulkPatchProps => {
  const name = formName.value || formName.isDirty ? formName.value : null
  const startAt = formStartAt.value || null
  const endAt = formEndAt.value || null
  const notes = formNotes.value || formNotes.isDirty ? formNotes.value : null

  // For amplitude tracking purposes, we have to keep the original note values
  // in mind whether they were unique or not, but also listen to any changes
  // the user makes before submitting the form.
  const mixedValueNotes = uniqNotesLength > 1 && !formNotes.isDirty ? 1 : 0

  const locationId = formLocationId.value ? String(formLocationId.value) : null
  const workAreaId = formWorkAreaId.value ? String(formWorkAreaId.value) : null

  return {
    shifts,
    name,
    startAt,
    endAt,
    extraHours: formExtraHours,
    notes,
    locationId,
    workAreaId,
    mixedValueNotes,
  }
}

const useEditShifts = (
  {
    shifts,
    name,
    startAt,
    endAt,
    notes,
    extraHours,
    locationId,
    workAreaId,
    mixedValueNotes,
  }: BulkPatchProps,
  plannedBreakForms: PlannedBreakForm[],
  through: 'modal' | 'side_panel'
) => {
  const [{ shiftsRows }, dispatchShiftsRows] = useShiftsRows()
  const dispatchSelection = useSelectionDispatch()

  const [{ view }] = useSchedule()
  const { refreshStats } = useRefreshStats()

  const shiftIds = shifts.map((s) => s.id)

  const {
    company: { id: currentCompanyId },
  } = useCurrentSession()

  const [bulkPatchShifts, { loading }] = useMutation(ShiftManagementBulkPatchShiftsDocument, {
    extractorFn: (data) => data.shiftManagementMutations.bulkPatchShiftmanagementShift,
  })

  const onEdit = async (closePortal?: ClosePortalFn) => {
    const plannedBreaks = shifts.length === 1 ? toBreaksPayload(plannedBreakForms) : []

    const result = await bulkPatchShifts({
      variables: {
        companyId: currentCompanyId,
        shiftIds,
        name,
        startAt,
        endAt,
        notes,
        extraHours,
        locationId,
        workAreaId,
        plannedBreaks,
      },
    })

    const handled = handleResult(result, (_errors) => {})
    if (handled.success) {
      const shiftsData = handled.data?.shifts || []
      const newShifts = shiftsData.map((shift) =>
        Shift.buildFromGraphQL({
          shift,
          employeeFullName: shift.employee?.fullName || '',
          employeeAvatar: shift.employee?.avatar?.url || '',
          employeeLocationId: shift.employee?.locationId ?? '0',
        })
      )
      dispatchShiftsRows({ type: 'DESTROY_SHIFTS', shifts, view })
      dispatchShiftsRows({ type: 'ADD_SHIFTS', shifts: newShifts, view })
      dispatchSelection({ type: 'CLEAR_SELECTION' })

      refreshStats(shiftsRows)

      await trackEditShiftSubmit({
        shifts,
        name,
        startAt,
        endAt,
        notes,
        extraHours,
        locationId,
        workAreaId,
        mixedValueNotes,
        view,
        assignedToModified: false,
        originalEmployeeIncluded: false,
        totalNumberOfAssignedEmployees: 0,
        through,
      })

      if (closePortal) closePortal()

      app.setFlash({
        type: 'correct',
        message: i18n.t('shift_management.actions.edit_shifts_success'),
      })
    }
  }

  return {
    onEdit,
    submitting: loading,
  }
}

export default useEditShifts
